import React from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import { ArrowUpRight } from "lucide-react";

const FOUNDER_BG = "https://static.prod-images.emergentagent.com/jobs/639a096e-1f65-4607-b91b-d74c23fcac10/images/f91d33950794083b9b00430f5b4b327cac526421b21aa9b7624d61306b33dd15.png";
const FOUNDER_PHOTO = `${process.env.REACT_APP_BACKEND_URL}/api/site-image/founder-depression`;

const Section = ({ children, className = "" }) => (
    <div className={`max-w-3xl mx-auto px-6 lg:px-0 ${className}`}>{children}</div>
);

const Story = () => {
    return (
        <div data-testid="story-page" className="bg-black">
            {/* HERO */}
            <section className="relative py-24 md:py-32 overflow-hidden border-b border-white/10">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-20"
                    style={{ backgroundImage: `url(${FOUNDER_BG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-8 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                            <span className="h-px w-12 bg-red-600" />
                            The Founder's Story
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85]">
                            I survived a night
                            <br />
                            I wasn't <span className="text-red-600">supposed to.</span>
                        </h1>
                        <p className="mt-10 max-w-2xl text-white/80 text-lg leading-relaxed">
                            This is not a polished story. This is a survival report. From the dark, for
                            anyone still in it.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex justify-center lg:justify-end">
                        <div className="relative">
                            <div className="absolute -inset-3 bg-black/40 -z-10" style={{ transform: "translate(12px, 12px)" }} />
                            <img
                                src={FOUNDER_PHOTO}
                                alt="Tim — MindWorks founder breaking through depression"
                                className="w-72 sm:w-80 md:w-96 border border-white/10 object-cover"
                                data-testid="story-founder-photo"
                            />
                            <div className="absolute -bottom-3 left-0 right-0 bg-black/90 border border-white/10 text-center py-2">
                                <div className="font-stencil text-xs tracking-[0.3em] text-white/80">FOUNDER · MINDWORKS</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BODY */}
            <article className="py-20 md:py-28 text-white/85 leading-[1.85] text-lg">
                <Section>
                    <p>There was a season in my life where the battle in my mind became louder than anything else around me.</p>
                    <p className="mt-6">From the outside, I may have looked functional. I could smile. I could talk. I could keep moving.</p>
                    <p className="mt-6">But internally, I was collapsing.</p>
                </Section>

                <Section className="mt-16">
                    <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight text-red-600">
                        Depression is not just "feeling sad."
                    </h2>
                    <p className="mt-8">It is darkness. It is exhaustion. It is numbness. It is feeling trapped inside your own mind while desperately trying to survive your own thoughts.</p>
                    <p className="mt-6">And for me, that battle became so intense that I began battling suicidal ideation.</p>
                    <p className="mt-6">Thoughts started convincing me that people would be better off without me. That the pain would never end. That there was no way out.</p>
                    <p className="mt-6 italic text-white/70">The terrifying thing about depression is that eventually the mind can begin to believe its own lies.</p>
                </Section>

                <Section className="mt-16">
                    <p>And one night, the war inside my head became overwhelming.</p>
                    <p className="mt-6">I remember trying to get access to my gun.</p>
                    <p className="mt-6">That moment still shakes me when I think about it, because I truly believed I was at the end.</p>
                </Section>

                <Section className="mt-16 border-l-2 border-red-600 pl-8 py-6 bg-[#0a0a0a]">
                    <p>But in the middle of that darkness — when my thoughts were screaming death — there was another voice.</p>
                    <p className="mt-6 font-display text-3xl md:text-4xl tracking-tight uppercase text-white">
                        Quiet. Clear. Persistent.
                    </p>
                    <p className="mt-6">A voice telling me:</p>
                    <p className="mt-4 font-display text-3xl md:text-5xl text-red-600 tracking-tight">"Get help."</p>
                    <p className="mt-8">I believe with everything in me that it was the Holy Spirit.</p>
                    <p className="mt-4">Not condemnation. Not shame. Not fear.</p>
                    <p className="mt-4 font-display text-2xl tracking-wide uppercase">Rescue.</p>
                </Section>

                <Section className="mt-16">
                    <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">
                        God met me in the <span className="text-red-600">darkest moment</span> of my life.
                    </h2>
                    <p className="mt-8">And instead of letting me destroy myself, He led me toward help. The Holy Spirit led me to a mental health facility.</p>
                    <p className="mt-6">Walking into that place felt humiliating at first. I felt broken. Weak. Embarrassed.</p>
                    <p className="mt-6 italic">But what I later realized is this: it takes courage to admit you need help.</p>
                    <p className="mt-6">That facility was not the end of my story. It was the beginning of my healing.</p>
                    <p className="mt-6">God used doctors. Counselors. Rest. People. Wisdom. Time. Truth. And process. Because healing is often both spiritual and practical.</p>
                </Section>

                <Section className="mt-16">
                    <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">
                        The mind is a <span className="text-red-600">battlefield.</span>
                    </h2>
                    <p className="mt-8">Not metaphorically. Literally.</p>
                    <p className="mt-6">Your thoughts can either pull you toward life or drag you toward destruction. And when you're in depression long enough, you begin feeding thoughts that were never meant to lead you.</p>
                    <p className="mt-6">I had to learn how to fight differently. I had to learn:</p>
                    <ul className="mt-6 space-y-2 list-none">
                        <li className="flex gap-3"><span className="text-red-600 font-display">»</span> how stress affects the brain</li>
                        <li className="flex gap-3"><span className="text-red-600 font-display">»</span> how anxiety rewires thinking</li>
                        <li className="flex gap-3"><span className="text-red-600 font-display">»</span> how isolation deepens depression</li>
                        <li className="flex gap-3"><span className="text-red-600 font-display">»</span> how intrusive thoughts grow when entertained</li>
                        <li className="flex gap-3"><span className="text-red-600 font-display">»</span> how fear alters the body</li>
                        <li className="flex gap-3"><span className="text-red-600 font-display">»</span> and how truth has to be repeatedly planted into the mind</li>
                    </ul>
                    <p className="mt-8">Most importantly, I had to learn how to take every thought captive.</p>
                    <p className="mt-6">Not every thought deserves agreement. Not every thought deserves your identity. Not every thought is telling you the truth.</p>
                    <p className="mt-6 font-display text-2xl tracking-tight uppercase text-red-600">
                        Some thoughts are lies trying to destroy you.
                    </p>
                </Section>

                <Section className="mt-16">
                    <p>So many people are suffering silently exactly the way I was.</p>
                    <p className="mt-6">Men. Teenagers. Fathers. Mothers. Pastors. Students.</p>
                    <p className="mt-6">People sitting in churches. People sitting at dinner tables. People scrolling on their phones at 2 AM trying to survive another night.</p>
                    <p className="mt-8 font-display text-3xl md:text-4xl uppercase tracking-tight">
                        That's why MindWorks <span className="text-red-600">exists.</span>
                    </p>
                </Section>

                <Section className="mt-16">
                    <p>I want people to know:</p>
                    <div className="mt-6 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {[
                            "You are not crazy.",
                            "You are not weak.",
                            "You are not beyond healing.",
                            "You are not alone.",
                        ].map((line) => (
                            <div key={line} className="bg-black p-6 font-display text-2xl tracking-tight uppercase">
                                {line}
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 italic">And asking for help does not make you less of a man. Sometimes asking for help is the first real act of strength.</p>
                </Section>

                <Section className="mt-16">
                    <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight">
                        Today, my story is no longer about <span className="text-red-600">almost dying.</span>
                    </h2>
                    <p className="mt-8 font-display text-2xl tracking-tight">It's about helping people live.</p>
                    <p className="mt-6">It's about helping people understand the war happening in their minds. It's about teaching people how to fight back. It's about helping people heal — spiritually, mentally, emotionally, and practically.</p>
                    <p className="mt-6">Most importantly, it's about pointing people toward Jesus. Because when I was at my absolute lowest, when darkness convinced me there was no future left for me, God intervened. Not with condemnation. With mercy.</p>
                </Section>

                <Section className="mt-16 text-center">
                    <Logo size={64} />
                    <p className="mt-8 font-display text-3xl md:text-4xl uppercase tracking-tight">
                        My mission is simple:
                    </p>
                    <p className="mt-6 text-white/80 max-w-xl mx-auto">
                        To walk into dark places and remind people that hope is still alive.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to="/companion" className="btn-primary" data-testid="story-cta-companion">
                            Talk to the Companion <ArrowUpRight size={18} />
                        </Link>
                        <Link to="/help" className="btn-ghost" data-testid="story-cta-help">
                            Get Help Now
                        </Link>
                    </div>
                </Section>
            </article>
        </div>
    );
};

export default Story;
